<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NativeSelect from 'dashboard/components-next/native-select/NativeSelect.vue';
import ClosingNoteCategoryDialog from './ClosingNoteCategoryDialog.vue';

const { t } = useI18n();
const store = useStore();
const { currentAccount, updateAccount } = useAccount();

const categoryDialogRef = ref(null);
const searchQuery = ref('');
const isUpdatingMode = ref(false);

const categories = useMapGetter(
  'closingNoteCategories/getClosingNoteCategories'
);
const uiFlags = useMapGetter('closingNoteCategories/getUIFlags');

const modeOptions = computed(() => [
  {
    id: 'optional',
    label: t('CONVERSATION_WORKFLOW.CLOSING_NOTE.MODE.OPTIONAL'),
  },
  {
    id: 'category_required',
    label: t('CONVERSATION_WORKFLOW.CLOSING_NOTE.MODE.CATEGORY_REQUIRED'),
  },
  {
    id: 'all_required',
    label: t('CONVERSATION_WORKFLOW.CLOSING_NOTE.MODE.ALL_REQUIRED'),
  },
  {
    id: 'disabled',
    label: t('CONVERSATION_WORKFLOW.CLOSING_NOTE.MODE.DISABLED'),
  },
]);

const selectedMode = computed({
  get() {
    return currentAccount.value?.settings?.closing_notes_mode || 'disabled';
  },
  async set(value) {
    isUpdatingMode.value = true;
    try {
      await updateAccount({ closing_notes_mode: value }, { silent: true });
      useAlert(t('CONVERSATION_WORKFLOW.CLOSING_NOTE.MODE.SUCCESS'));
    } catch {
      useAlert(t('CONVERSATION_WORKFLOW.CLOSING_NOTE.MODE.ERROR'));
    } finally {
      isUpdatingMode.value = false;
    }
  },
});

const filteredCategories = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return categories.value;

  return categories.value.filter(category => {
    return [category.name, category.description]
      .filter(Boolean)
      .some(value => value.toLowerCase().includes(query));
  });
});

function openCreateDialog() {
  categoryDialogRef.value?.open();
}

function openEditDialog(category) {
  categoryDialogRef.value?.open(category);
}

async function handleSubmitCategory(payload) {
  try {
    if (payload.id) {
      await store.dispatch('closingNoteCategories/update', payload);
      useAlert(
        t('CONVERSATION_WORKFLOW.CLOSING_NOTE.CATEGORIES.UPDATE.SUCCESS')
      );
    } else {
      await store.dispatch('closingNoteCategories/create', payload);
      useAlert(
        t('CONVERSATION_WORKFLOW.CLOSING_NOTE.CATEGORIES.CREATE.SUCCESS')
      );
    }
  } catch {
    useAlert(t('CONVERSATION_WORKFLOW.CLOSING_NOTE.CATEGORIES.ERROR'));
  }
}

async function handleDeleteCategory(category) {
  const confirmed = window.confirm(
    t('CONVERSATION_WORKFLOW.CLOSING_NOTE.CATEGORIES.DELETE.CONFIRM', {
      name: category.name,
    })
  );
  if (!confirmed) return;

  try {
    await store.dispatch('closingNoteCategories/delete', category.id);
    useAlert(t('CONVERSATION_WORKFLOW.CLOSING_NOTE.CATEGORIES.DELETE.SUCCESS'));
  } catch {
    useAlert(t('CONVERSATION_WORKFLOW.CLOSING_NOTE.CATEGORIES.ERROR'));
  }
}

onMounted(() => {
  store.dispatch('closingNoteCategories/get');
});
</script>

<template>
  <div
    class="flex flex-col w-full outline-1 outline outline-n-container rounded-xl bg-n-solid-2 divide-y divide-n-weak"
  >
    <div class="flex flex-col gap-2 px-5 py-4">
      <h3 class="text-heading-2 text-n-slate-12">
        {{ t('CONVERSATION_WORKFLOW.CLOSING_NOTE.TITLE') }}
      </h3>
      <p class="mb-0 text-body-para text-n-slate-11">
        {{ t('CONVERSATION_WORKFLOW.CLOSING_NOTE.DESCRIPTION') }}
      </p>
    </div>

    <div class="flex flex-col gap-5 px-5 py-4">
      <div class="max-w-xl">
        <label class="mb-2 block text-sm font-medium text-n-slate-12">
          {{ t('CONVERSATION_WORKFLOW.CLOSING_NOTE.MODE.LABEL') }}
        </label>
        <NativeSelect v-model="selectedMode" :disabled="isUpdatingMode">
          <option
            v-for="option in modeOptions"
            :key="option.id"
            :value="option.id"
          >
            {{ option.label }}
          </option>
        </NativeSelect>
      </div>

      <div class="flex items-center justify-between gap-4">
        <Button
          icon="i-lucide-plus"
          :label="$t('CONVERSATION_WORKFLOW.CLOSING_NOTE.CATEGORIES.ADD')"
          @click="openCreateDialog"
        />
        <div class="w-full max-w-sm">
          <Input
            v-model="searchQuery"
            icon="i-lucide-search"
            :placeholder="
              $t('CONVERSATION_WORKFLOW.CLOSING_NOTE.CATEGORIES.SEARCH')
            "
          />
        </div>
      </div>

      <div class="overflow-hidden rounded-xl border border-n-weak">
        <table class="min-w-full divide-y divide-n-weak">
          <thead class="bg-n-alpha-black2 text-left text-sm text-n-slate-11">
            <tr>
              <th class="px-4 py-3 font-medium">
                {{
                  t('CONVERSATION_WORKFLOW.CLOSING_NOTE.CATEGORIES.TABLE.NAME')
                }}
              </th>
              <th class="px-4 py-3 font-medium">
                {{
                  t(
                    'CONVERSATION_WORKFLOW.CLOSING_NOTE.CATEGORIES.TABLE.DESCRIPTION'
                  )
                }}
              </th>
              <th class="px-4 py-3 font-medium text-right">
                {{
                  t(
                    'CONVERSATION_WORKFLOW.CLOSING_NOTE.CATEGORIES.TABLE.ACTIONS'
                  )
                }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak bg-n-solid-1">
            <tr
              v-for="category in filteredCategories"
              :key="category.id"
              class="text-sm text-n-slate-12"
            >
              <td class="px-4 py-3 font-medium">{{ category.name }}</td>
              <td class="px-4 py-3 text-n-slate-11">
                {{ category.description || ' ' }}
              </td>
              <td class="px-4 py-3">
                <div class="flex justify-end gap-2">
                  <Button
                    link
                    sm
                    :label="
                      $t(
                        'CONVERSATION_WORKFLOW.CLOSING_NOTE.CATEGORIES.TABLE.EDIT'
                      )
                    "
                    @click="openEditDialog(category)"
                  />
                  <Button
                    link
                    sm
                    ruby
                    :label="
                      $t(
                        'CONVERSATION_WORKFLOW.CLOSING_NOTE.CATEGORIES.TABLE.DELETE'
                      )
                    "
                    @click="handleDeleteCategory(category)"
                  />
                </div>
              </td>
            </tr>
            <tr
              v-if="!uiFlags.isFetching && filteredCategories.length === 0"
              class="text-sm text-n-slate-11"
            >
              <td class="px-4 py-6 text-center" colspan="3">
                {{ t('CONVERSATION_WORKFLOW.CLOSING_NOTE.CATEGORIES.EMPTY') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <ClosingNoteCategoryDialog
      ref="categoryDialogRef"
      @submit="handleSubmitCategory"
    />
  </div>
</template>
